import Fab                                                                               from '@material-ui/core/Fab';
import {createStyles, makeStyles, Theme}                                                 from '@material-ui/core/styles';
import AddIcon                                                                           from '@material-ui/icons/Add';
import React, {Dispatch, MutableRefObject, SetStateAction, useEffect, useState, useMemo} from 'react';
import {ListManager}                                                                     from "react-beautiful-dnd-grid";
import styled                                                                            from "styled-components";

import {Asset, Instrument, MarketWidgetIdentifier}                 from "../../../../api/api";
import {useApi}                                                    from "../../../../hooks/useApi";
import {useProfile}                                                from '../../../../hooks/useProfile';
import {CancelOrderBookRequest, Order, OrderBookSnapshotResponse}  from '../../../../trade/types';
import {DialogState, OrderDialog}                                  from "../orders/OrderDialog";
import {MarketWidget, MarketWidgetElement, MarketWidgetElementRow} from "./MarketWidget";
import {AddMarketWidgetModal}                                      from "./MarketWidgetModal";

const Container = styled.div`
  display          : flex;
  justify-content  : flex-start;
  flex             : 1 0 auto;
  flex-wrap        : wrap;
`;

interface AddMarketWidgetButtonProps {
  setModalOpen: Dispatch<SetStateAction<boolean>>
}

const MarketWidgetButtonStyles = makeStyles((theme: Theme) =>
    createStyles({
      root: {'margin-top': '104px'},
    }),
)

const AddMarketWidgetButton = (p: AddMarketWidgetButtonProps) => {
  const classes = MarketWidgetButtonStyles();

  return (
    <Fab color="primary" aria-label="add" className={classes.root} onClick={() => {p.setModalOpen(true);}}>
      <AddIcon />
    </Fab>
  )
}

export interface MarketWidgetPanelProps {
  assetIdMap                      : {[assetId: number]: Asset} | undefined
  clientTradableInstruments       : Instrument[] | undefined
  loadMarketWidgets               : () => void
  marketPriceWebSocket            : MutableRefObject<WebSocket | null>
  marketWidgets                   : MarketWidgetIdentifier[] | undefined
  orderBooks                      : { [instrumentID: number]: OrderBookSnapshotResponse }
  requestIDtoInstrumentID         : { [requestID: string]: number }
  setMarketWidgets                : (newMarketWidgets: MarketWidgetIdentifier[]) => void
  setRequestIDtoInstrumentID      : Dispatch<SetStateAction<{ [requestID: string]: number }>>
  tradableAssets                  : Asset[] | undefined
  orderDialogState?               : DialogState
  setOrderDialogState?            : Dispatch<SetStateAction<DialogState>>
  setOrders?                      : Dispatch<SetStateAction<{ [orderID: string]: Order }>>
}

export const MarketWidgetPanel = (p: MarketWidgetPanelProps) => {
  // State
  const { isAuth }                                                = useApi(),
        { client }                                                = useProfile(),
        [ newMarketWidgetModalOpen, setNewMarketWidgetModalOpen ] = useState<boolean>(false),
        addMarketWidgetModal                                      = useMemo(() => <AddMarketWidgetModal
                                                                                              open={newMarketWidgetModalOpen}
                                                                                              setOpen={setNewMarketWidgetModalOpen}
                                                                                              marketWidgets={p.marketWidgets}
                                                                                              setMarketWidgets={p.setMarketWidgets}
                                                                                              tradableAssets={p.tradableAssets}
                                                                                              clientTradableInstruments={p.clientTradableInstruments}
                                                                                              assetIdMap={p.assetIdMap}
                                                                                          />,[newMarketWidgetModalOpen,setNewMarketWidgetModalOpen,p.marketWidgets,p.setMarketWidgets,p.tradableAssets,p.clientTradableInstruments,p.assetIdMap])
  
  // Handlers
  const onDragEnd = (sourceIndex: number, destinationIndex: number) => {
    if (sourceIndex!==0 || destinationIndex!==0){
      
      const result = Array.from(p.marketWidgets || []);

      if (sourceIndex === result.length - 1) {return result;}
    
      let mutableDestinationIndex: number = destinationIndex;
      if (mutableDestinationIndex === result.length - 1) {
        mutableDestinationIndex -= 1;
      }
      
      const [removed] = result.splice(sourceIndex, 1);
      result.splice(mutableDestinationIndex, 0, removed);

      if (isAuth) {
        p.setMarketWidgets(result);
      }
  }
  };

  const onCloseCallback = (event: MouseEvent) => {
    if (event && event.target) {
      let items:                    MarketWidgetIdentifier[]  = (p.marketWidgets) ? p.marketWidgets : [],
          instrumentTickerToRemove: string                    = (event.target as HTMLButtonElement).id,
          instrumentIDToRemove:     number                    = items.filter((item) => {return item.instrumentTicker === instrumentTickerToRemove})[0].instrumentID,
          requestIDToRemove:        string | undefined        = Object.keys(p.requestIDtoInstrumentID).find(k => p.requestIDtoInstrumentID[k] === instrumentIDToRemove);

      let itemsCopy = JSON.parse(JSON.stringify(items));
      itemsCopy.forEach( (item:MarketWidgetIdentifier, index:number) => {
        if(item.instrumentTicker === instrumentTickerToRemove) {
          itemsCopy.splice(index, 1);
        }
      });

      items = JSON.parse(JSON.stringify(itemsCopy))
      if (isAuth) {
        p.setMarketWidgets(items);
      }
      
      onDragEnd(0, 0);  // Consider better way to re-render ListManager component

      if (requestIDToRemove && p.marketPriceWebSocket.current) {
        const cancelOrderBook: CancelOrderBookRequest = {
          type: "request_book_cancel",
          request_id: requestIDToRemove
        };
        console.log("Requesting order book cancel for instrument " + instrumentTickerToRemove);
        p.marketPriceWebSocket.current.send(JSON.stringify(cancelOrderBook));
      }
    }
  };

  useEffect(() => {
    if (isAuth) {
      p.loadMarketWidgets();
    }
  }, [isAuth, p]);
  
  return (
    <>
      <Container>
        {addMarketWidgetModal}
        {p.marketWidgets && <ListManager
          items={p.marketWidgets}
          direction="horizontal"
          maxItems={1}
          render={(item: MarketWidgetIdentifier) => (item.instrumentTicker !== "ADD_MARKET_WIDGET") ?
            <MarketWidget
              key={item.instrumentTicker}
              quoteCurrencyTicker={item.quoteCurrencyTicker}
              quantityTicker={item.quantityAssetTicker}
              baseAssetTicker={item.baseAssetTicker}
              client={client}
              instrumentID={item.instrumentID}
              instrumentTicker={item.instrumentTicker}
              marketPriceWebSocket={p.marketPriceWebSocket}
              onCloseCallback={onCloseCallback}
              orderBook={p.orderBooks[item.instrumentID]}
              setRequestIDtoInstrumentID={p.setRequestIDtoInstrumentID}
              setOrders={p.setOrders}
            />
            :
            <MarketWidgetElement>
              <MarketWidgetElementRow>
                <AddMarketWidgetButton setModalOpen={setNewMarketWidgetModalOpen}/>
              </MarketWidgetElementRow>
            </MarketWidgetElement>
          }
          onDragEnd={onDragEnd}
        />}
      </Container>
      {p.orderDialogState && p.setOrderDialogState && <OrderDialog 
                                                          orderDialogState={p.orderDialogState}
                                                          onClose={() => {
                                                            if (p.setOrderDialogState) {
                                                              p.setOrderDialogState(previousState => {return {...previousState, isOpen: false}});
                                                            }
                                                          }}
                                                        />
      }
    </>
  )
}
